import { UnfurlItem } from '@lib/external/unfurl';
import { DuplicateCheckable } from '@lib/utils/chooseMediaUtils';
import { pluralize } from '@lib/utils/units';
import { useCallback, useMemo } from 'react';
import css from '../components/ChooseMediaModal/ChooseMediaModal/components/MediaDataGrid.module.scss';
import { useDuplicateChecker } from './useDuplicateChecker';

// TypeScript interfaces for DataGrid callbacks
export interface DataGridRowParams<T = UnfurlItem> {
  row: T;
  id: string | number;
  isSelected?: boolean;
  isVisible?: boolean;
}

export interface DataGridSelectionModel {
  type: 'include' | 'exclude';
  ids: Set<string | number> | (string | number)[];
}

export interface UseDataGridSelectionProps<T extends DuplicateCheckable & { id: string }> {
  data: T[];
  existingUniqueIds: Record<string, string>;
  onSelectionChange: (selectedIds: string[]) => void;
  selectedIds?: string[];
  errorIds?: string[];
}

export interface UseDataGridSelectionReturn<T = UnfurlItem> {
  duplicateIds: string[];
  isRowSelectable: (params: DataGridRowParams<T>) => boolean;
  getRowClassName: (params: DataGridRowParams<T>) => string;
  handleSelectionChange: (newSelection: DataGridSelectionModel) => void;
  rowSelectionModel: { type: 'include'; ids: Set<string> };
  getFooterRowSelectedText: (count: number) => string;
}

/**
 * Custom hook for managing DataGrid selection logic with duplicate handling
 * Extracts complex selection logic from MediaDataGrid component
 * Generic to work with any type that has uniqueId and id properties
 */
export const useDataGridSelection = <T extends DuplicateCheckable & { id: string } = UnfurlItem>({
  data,
  existingUniqueIds,
  onSelectionChange,
  selectedIds = [],
  errorIds = [],
}: UseDataGridSelectionProps<T>): UseDataGridSelectionReturn<T> => {
  const { isDuplicate } = useDuplicateChecker<T>({
    data,
    existingUniqueIds,
  });

  // Get duplicate IDs for visual selection model
  const duplicateIds = useMemo(() => {
    return data.filter(isDuplicate).map((item) => item.id);
  }, [data, isDuplicate]);

  // Pre-compute duplicate IDs as a Set for O(1) lookup in handleSelectionChange
  const duplicateIdsSet = useMemo(() => new Set(duplicateIds), [duplicateIds]);

  // Pre-compute error IDs as a Set for O(1) lookup
  const errorIdsSet = useMemo(() => new Set(errorIds), [errorIds]);

  // Function to determine if a row can be selected (non-duplicates and non-errors only)
  const isRowSelectable = useCallback(
    (params: DataGridRowParams<T>) => {
      return !isDuplicate(params.row) && !errorIdsSet.has(String(params.id));
    },
    [isDuplicate, errorIdsSet],
  );

  // Function to apply CSS classes to rows (duplicates/errors are disabled)
  const getRowClassName = useCallback(
    (params: DataGridRowParams<T>) => {
      if (errorIdsSet.has(String(params.id))) return css.errorRowDisabled;
      if (isDuplicate(params.row)) return css.duplicateRowDisabled;
      return '';
    },
    [isDuplicate, errorIdsSet],
  );

  // Extract selected IDs from DataGrid selection model
  const extractSelectedIds = useCallback((selection: DataGridSelectionModel): string[] => {
    if (selection.type === 'include') {
      const ids = selection.ids instanceof Set ? Array.from(selection.ids) : selection.ids;
      return ids.map((id) => String(id));
    }
    return [];
  }, []);

  // Handle selection change with duplicate and error filtering
  // Uses duplicateIdsSet and errorIdsSet for O(1) lookup instead of iterating through data
  const handleSelectionChange = useCallback(
    (newSelection: DataGridSelectionModel) => {
      const allSelectedIds = extractSelectedIds(newSelection);
      // Fast O(1) Set lookup instead of iterating through data array
      const filteredIds = allSelectedIds.filter(
        (id) => !duplicateIdsSet.has(id) && !errorIdsSet.has(id),
      );
      onSelectionChange(filteredIds);
    },
    [extractSelectedIds, onSelectionChange, duplicateIdsSet, errorIdsSet],
  );

  // Memoized row selection model - returns stable reference when deps don't change
  // TODO: Performance optimization - consider removing duplicateIds from selection model.
  // Currently duplicates appear visually "checked" but this adds overhead on selection changes.
  // Removing them would improve selection performance but duplicates would lose the checked appearance.
  const rowSelectionModel = useMemo(
    () => ({
      type: 'include' as const,
      ids: new Set([...selectedIds, ...duplicateIds]),
    }),
    [selectedIds, duplicateIds],
  );

  // Get footer text for selected rows
  const getFooterRowSelectedText = useCallback(
    (count: number) => {
      // Calculate actual non-duplicate count
      const duplicateCount = duplicateIds.length;
      const nonDuplicateCount = count - duplicateCount;
      return `${pluralize(nonDuplicateCount, 'item')} to be added to your library`;
    },
    [duplicateIds],
  );

  return {
    duplicateIds,
    isRowSelectable,
    getRowClassName,
    handleSelectionChange,
    rowSelectionModel,
    getFooterRowSelectedText,
  };
};
